import type { ReactNode } from 'react';

interface CardProps {
  /**
   * Position in the page's card list, formatted for the badge. Optional: a card
   * that stands alone needs no number.
   *
   * Passed in rather than counted here, because the number has to come from the
   * list that decides which cards exist. Every card in this app used to carry
   * its own hard-coded badge — 27 of them across 16 files — which is why the
   * user page numbered itself `01`, `02`, `01`: cards appear and disappear with
   * the reader's capabilities, and a literal cannot know that.
   */
  num?: number;
  title: string;
  /** Small right-aligned annotation: the data source, a format, a limit. */
  meta?: ReactNode;
  accent?: 'blue' | 'green';
  /** Extra head content, right-aligned before `meta` — a status pill. */
  headExtra?: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  children: ReactNode;
}

export function Card({
  num,
  title,
  meta,
  accent,
  headExtra,
  className,
  style,
  children,
}: CardProps) {
  return (
    <section
      className={['epi-card', accent && `tone-${accent}`, className]
        .filter(Boolean)
        .join(' ')}
      style={style}
    >
      <div className="epi-card-head">
        {num !== undefined && (
          <span className="num">{String(num).padStart(2, '0')}</span>
        )}
        <span className="title">{title}</span>
        {headExtra}
        {meta !== undefined && <span className="meta">{meta}</span>}
      </div>
      {children}
    </section>
  );
}
